/**
 * useWorkoutForm — Encapsulates all workout form state from record.tsx:
 *   entries, workout name, session notes, superset groups, timer state.
 */
import { useState, useCallback, useEffect, useRef } from 'react';
import { Platform, Alert, LayoutAnimation, UIManager } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import type { ExerciseEntry } from '@/components/record';

if (Platform.OS === 'android' && UIManager.setLayoutAnimationEnabledExperimental) {
  UIManager.setLayoutAnimationEnabledExperimental(true);
}

function showAlert(title: string, message: string) {
  if (Platform.OS === 'web') {
    alert(`${title}: ${message}`);
  } else {
    Alert.alert(title, message);
  }
}

export function useWorkoutForm() {
  const [exerciseEntries, setExerciseEntries] = useState<ExerciseEntry[]>([]);
  const [workoutName, setWorkoutName] = useState('');
  const [sessionNotes, setSessionNotes] = useState('');
  const [supersetGroups, setSupersetGroups] = useState<Record<string, string[]>>({});
  const [workoutTimerRunning, setWorkoutTimerRunning] = useState(false);
  const [workoutStartTime, setWorkoutStartTime] = useState<number | null>(null);
  const [workoutDate, setWorkoutDate] = useState(new Date());
  const [workoutDraftId, setWorkoutDraftId] = useState(() => `workout-draft-${Date.now()}`);
  const [supersetMode, setSupersetMode] = useState(false);
  const [activeSupersetGroupId, setActiveSupersetGroupId] = useState<string | null>(null);

  // Auto-save workout state to AsyncStorage so it survives backgrounding/crashes
  const AUTOSAVE_KEY = 'duel:workout_autosave';
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    // Restore autosave on mount
    AsyncStorage.getItem(AUTOSAVE_KEY).then((raw) => {
      if (!raw) return;
      try {
        const saved = JSON.parse(raw);
        if (saved.exerciseEntries?.length > 0) {
          setExerciseEntries(saved.exerciseEntries);
          if (saved.workoutName) setWorkoutName(saved.workoutName);
          if (saved.sessionNotes) setSessionNotes(saved.sessionNotes);
          if (saved.workoutDate) setWorkoutDate(new Date(saved.workoutDate));
        }
      } catch { /* ignore corrupt saves */ }
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Debounce auto-save: write 1s after last change
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    if (exerciseEntries.length === 0) {
      // Clear save when form is empty (after submit or manual clear)
      AsyncStorage.removeItem(AUTOSAVE_KEY).catch(() => {});
      return;
    }
    saveTimerRef.current = setTimeout(() => {
      AsyncStorage.setItem(AUTOSAVE_KEY, JSON.stringify({
        exerciseEntries,
        workoutName,
        sessionNotes,
        workoutDate: workoutDate.toISOString(),
      })).catch(() => {});
    }, 1000);
    return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); };
  }, [exerciseEntries, workoutName, sessionNotes, workoutDate]);

  // Auto-start workout timer on first set logged
  useEffect(() => {
    if (
      !workoutTimerRunning &&
      exerciseEntries.some(
        (e) =>
          e.exercise &&
          (e.weight ||
            e.reps ||
            (e.setDetails && e.setDetails.some((s) => s.weight || s.reps))),
      )
    ) {
      setWorkoutTimerRunning(true);
      setWorkoutStartTime(Date.now());
    }
  }, [exerciseEntries]); // eslint-disable-line react-hooks/exhaustive-deps

  const addExerciseEntry = useCallback(() => {
    const newEntry: ExerciseEntry = {
      id: Date.now().toString() + Math.random().toString(36).slice(2, 6),
      exercise: null,
      weight: '',
      reps: '',
      sets: '1',
      isPR: false,
      videoUri: null,
      notes: '',
      supersetGroupId: supersetMode && activeSupersetGroupId ? activeSupersetGroupId : undefined,
    };
    setExerciseEntries((prev) => [...prev, newEntry]);
    // If superset mode is on, also track this entry in the group
    if (supersetMode && activeSupersetGroupId) {
      setSupersetGroups((prev) => ({
        ...prev,
        [activeSupersetGroupId!]: [...(prev[activeSupersetGroupId!] || []), newEntry.id],
      }));
    }
  }, [supersetMode, activeSupersetGroupId]);

  const removeExerciseEntry = useCallback((id: string) => {
    setExerciseEntries((prev) => prev.filter((entry) => entry.id !== id));
  }, []);

  const updateExerciseEntry = useCallback((id: string, updates: Partial<ExerciseEntry>) => {
    setExerciseEntries((prev) =>
      prev.map((entry) => (entry.id === id ? { ...entry, ...updates } : entry)),
    );
  }, []);

  // Superset toggle: turn on to start a group, exercises added while on get bracketed.
  // Turn off to close the group.
  const toggleSupersetMode = useCallback(() => {
    if (supersetMode) {
      // Turning off — close the active group
      // If the group has < 2 entries, disband it
      if (activeSupersetGroupId) {
        const groupEntries = supersetGroups[activeSupersetGroupId] || [];
        if (groupEntries.length < 2) {
          // Remove the group — unmark entries
          setExerciseEntries((prev) =>
            prev.map((e) =>
              e.supersetGroupId === activeSupersetGroupId
                ? { ...e, supersetGroupId: undefined }
                : e,
            ),
          );
          setSupersetGroups((prev) => {
            const updated = { ...prev };
            delete updated[activeSupersetGroupId!];
            return updated;
          });
        }
      }
      setSupersetMode(false);
      setActiveSupersetGroupId(null);
    } else {
      // Turning on — create a new group ID
      const groupId = `ss-${Date.now()}`;
      setSupersetMode(true);
      setActiveSupersetGroupId(groupId);
      setSupersetGroups((prev) => ({ ...prev, [groupId]: [] }));
    }
  }, [supersetMode, activeSupersetGroupId, supersetGroups]);

  // Legacy addSuperset kept for compatibility — now just toggles on
  const addSuperset = toggleSupersetMode;

  const addToSuperset = useCallback((groupId: string, entryId: string) => {
    setExerciseEntries((prev) =>
      prev.map((e) => (e.id === entryId ? { ...e, supersetGroupId: groupId } : e)),
    );
    setSupersetGroups((prev) => ({
      ...prev,
      [groupId]: [...(prev[groupId] || []), entryId],
    }));
  }, []);

  const removeFromSuperset = useCallback((entryId: string) => {
    setExerciseEntries((prev) =>
      prev.map((e) => (e.id === entryId ? { ...e, supersetGroupId: undefined } : e)),
    );
    setSupersetGroups((prev) => {
      const updated = { ...prev };
      for (const gid of Object.keys(updated)) {
        updated[gid] = updated[gid].filter((id) => id !== entryId);
        if (updated[gid].length < 2) {
          const remaining = updated[gid];
          delete updated[gid];
          if (remaining.length === 1) {
            setExerciseEntries((p) =>
              p.map((e) => (e.id === remaining[0] ? { ...e, supersetGroupId: undefined } : e)),
            );
          }
        }
      }
      return updated;
    });
  }, []);

  const reorderExercise = useCallback((fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex) return;
    LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
    setExerciseEntries(prev => {
      const copy = [...prev];
      const [moved] = copy.splice(fromIndex, 1);
      copy.splice(toIndex, 0, moved);
      return copy;
    });
  }, []);

  const resetForm = useCallback(() => {
    setExerciseEntries([]);
    setWorkoutName('');
    setSessionNotes('');
    setSupersetGroups({});
    setWorkoutTimerRunning(false);
    setWorkoutDraftId(`workout-draft-${Date.now()}`);
    // Clear autosave so next session starts fresh
    AsyncStorage.removeItem('duel:workout_autosave').catch(() => {});
  }, []);

  return {
    exerciseEntries,
    setExerciseEntries,
    workoutName,
    setWorkoutName,
    sessionNotes,
    setSessionNotes,
    supersetGroups,
    setSupersetGroups,
    workoutTimerRunning,
    setWorkoutTimerRunning,
    workoutStartTime,
    workoutDate,
    setWorkoutDate,
    workoutDraftId,
    setWorkoutDraftId,
    addExerciseEntry,
    removeExerciseEntry,
    updateExerciseEntry,
    addSuperset,
    toggleSupersetMode,
    supersetMode,
    addToSuperset,
    removeFromSuperset,
    reorderExercise,
    resetForm,
  };
}
